<script setup lang="ts">
import type { BlockHighlightsFragment, NewsThumbFragment } from '#graphql-operations'

interface HighlightsNewsProps {
  data: BlockHighlightsFragment
}

const props = defineProps<HighlightsNewsProps>()
const { locale } = useI18n()

const newsCarouselId = useId()

const { data: newsItems } = await useAsyncData(`highlightsNews-${props.data.id}`, async () => {
  const items = props.data.news.filter(Boolean) as NewsThumbFragment[]

  if (items.length) {
    return items
  }

  const res = await useGraphqlQuery('Entries', {
    limit: 3,
    section: ['news'],
    site: [locale.value],
    orderBy: 'date',
  })

  return (res.data.entries?.filter?.(Boolean) || []) as NewsThumbFragment[]
})
</script>

<template>
  <Section padded class="blockHighlights blockHighlightsNews">
    <div class="container layout-grid">
      <div v-if="data.heading" class="col-span-full">
        <Text class="typo-serif-3 text-center" :html="data.heading" />
      </div>
    </div>
    <div class="desktop-only">
      <ul class="container layout-grid">
        <li v-for="news in newsItems" :key="news.id" class="col-span-4">
          <CardNews :data="news" />
        </li>
      </ul>
    </div>
    <div class="mobile-only">
      <BlossomCarousel :id="newsCarouselId" as="ul" class="carousel size-full">
        <li
          v-for="news in newsItems"
          :key="news.id"
          data-blossom-slide
          class="slide flex-shrink-0"
        >
          <CardNews :data="news" />
        </li>
      </BlossomCarousel>
      <div class="container c-$accent-color mt-5 flex gap-4 items-center justify-between lg:mt-12.5">
        <BlossomPrev :for="newsCarouselId" :aria-label="$t('previous')">
          <ButtonIcon as="span" icon="chevron-left" />
        </BlossomPrev>
        <BlossomDots :for="newsCarouselId" class="flex gap-2 justify-center" />
        <BlossomNext :for="newsCarouselId" :aria-label="$t('next')">
          <ButtonIcon as="span" icon="chevron-right" />
        </BlossomNext>
      </div>
    </div>
    <ul class="container c-$accent-color mt-5 flex gap-2 w-full items-center justify-center lg:c-$primary-color lg:mt-12.5">
      <li v-for="link in data.links" :key="link!.id">
        <ButtonLink :link="link?.customLink" />
      </li>
    </ul>
  </Section>
</template>

<style scoped>
.blockHighlightsNews .carousel {
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  @screen lt-lg {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 100%;
    column-gap: calc(var(--spacing) * 4);
    padding-inline: calc(var(--spacing) * 4);
    scroll-padding-inline: calc(var(--spacing) * 4);
  }
}

.blockHighlightsNews :deep([data-blossom-slide]) {
  scroll-snap-align: start;
}

.blockHighlightsNews :deep(.blossom-dot) {
  background-color: var(--blossom-dot-color, currentColor);
  border-radius: 100%;

  inline-size: calc(var(--spacing) * 1.8);
  block-size: calc(var(--spacing) * 2);

  opacity: var(--blossom-dot-opacity, 0.35);

  &[aria-current='true'] {
    opacity: 1;
  }
}
</style>
